import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import TopNavigation from '../components/TopNavigation';
import { authService, VerifyEmailRequest, ResendVerificationRequest } from '@/services/authService';

interface EmailVerificationPageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
}

const EmailVerificationPage: React.FC<EmailVerificationPageProps> = ({
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onToggleMenu
}) => {
  const { theme } = useTheme();
  const { refreshUser } = useAuth();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(0);
  const verificationAttempted = useRef<boolean>(false);

  const themeClasses = isDarkMode ? 'text-white' : 'bg-white text-gray-900';

  useEffect(() => {
    // Extract token from URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const emailParam = urlParams.get('email');

    console.log('EmailVerificationPage mounted');
    console.log('Current URL:', window.location.href);
    console.log('Search params:', window.location.search);
    console.log('Token param:', tokenParam);
    console.log('Email param:', emailParam);

    if (tokenParam) {
      setToken(tokenParam);

      // Use ref to prevent double verification from React Strict Mode
      if (!verificationAttempted.current) {
        console.log('Starting verification - setting attempted flag');
        verificationAttempted.current = true;
        handleTokenVerification(tokenParam);
      } else {
        console.log('Verification already attempted, skipping');
      }
    }

    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  const handleTokenVerification = async (verificationToken: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('Starting email verification with token:', verificationToken);

      const verifyRequest: VerifyEmailRequest = {
        token: verificationToken
      };

      console.log('Sending verification request:', verifyRequest);
      const response = await authService.verifyEmail(verifyRequest);
      console.log('Verification response:', response);

      authService.setLastLoginMethod('email');
      setSuccess(`${response.message} You can now sign in to your account.`);

      // Start 5-second countdown and redirect
      setCountdown(5);
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            if (onNavigateToLogin) {
              onNavigateToLogin();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (error) {
      console.error('Email verification error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Email verification failed';

      // Provide more user-friendly error messages
      if (errorMessage.includes('Invalid or expired')) {
        setError('This verification link has expired or has already been used. Please request a new verification email.');
      } else if (errorMessage.includes('not found')) {
        setError('Verification token not found. Please check your email for the correct verification link.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      setError('Please enter your verification token');
      return;
    }

    await handleTokenVerification(token.trim());
  };

  const handleResendVerification = async () => {
    if (!email.trim()) {
      setError('Please enter your email address to resend verification');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const resendRequest: ResendVerificationRequest = {
        email: email.trim()
      };

      const response = await authService.resendVerification(resendRequest);

      setSuccess(`${response.message} Please check your email for the new verification link.`);

    } catch (error) {
      console.error('Resend verification error:', error);
      setError(error instanceof Error ? error.message : 'Failed to resend verification email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? '#1C1C1E' : 'white' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
      />

      {/* Main Content */}
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 py-8 pb-24 sm:pb-20">
        <div className="w-full max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-regular mb-2">Email Verification</h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Complete your account setup by verifying your email address
            </p>
          </div>

          {/* Verification Form */}
          <div className="max-w-md mx-auto">
            <div className={`p-8 rounded-lg border ${
              isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-gray-50 border-gray-200'
            }`}>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{error}</span>
                  </div>
                  {error.includes('expired') && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm">
                      <p className="font-medium mb-1">Need a new verification link?</p>
                      <p>Enter your email address below and click "Resend Verification Email" to get a fresh verification link.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <span className="text-sm">{success}</span>
                      {countdown > 0 && (
                        <div className="mt-2 text-sm font-medium">
                          Redirecting you to the login page in {countdown} seconds...
                          <button
                            onClick={() => setCountdown(0)}
                            className="ml-2 underline hover:no-underline text-green-600"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="mb-6 flex items-center justify-center p-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Verifying email...
                  </span>
                </div>
              )}

              {/* Manual Token Entry */}
              {!success && (
                <>
                  <form className="space-y-4 mb-6" onSubmit={handleManualVerification}>
                    <div>
                      <label htmlFor="token" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Verification Token
                      </label>
                      <input
                        type="text"
                        id="token"
                        name="token"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        className={`w-full px-3 py-2 rounded-full border ${
                          isDarkMode
                            ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                            : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Enter your verification token"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white transition-colors ${
                        isLoading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                      }`}
                    >
                      {isLoading ? 'Verifying...' : 'Verify Email'}
                    </button>
                  </form>

                  {/* Divider */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className={`w-full border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className={`px-2 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                        Didn't receive the email?
                      </span>
                    </div>
                  </div>

                  {/* Resend Verification */}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full px-3 py-2 rounded-full border ${
                          isDarkMode
                            ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                            : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Enter your email address"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isLoading}
                      className={`w-full flex justify-center py-2 px-4 border border-gray-300 rounded-full shadow-sm text-sm font-medium transition-colors ${
                        isLoading
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : isDarkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      {isLoading ? 'Sending...' : 'Resend Verification Email'}
                    </button>
                  </div>
                </>
              )}

              {/* Back to Login */}
              <div className="mt-6 text-center">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Already verified?{' '}
                  <button
                    onClick={() => onNavigateToLogin?.()}
                    className={`font-medium hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
